/**
 * <image-lightbox> — a full-screen image overlay.
 *
 * Markup: snippets/image-lightbox.liquid. Triggers are any control carrying
 * `data-lightbox-trigger="<lightbox id>"` plus the image data attributes;
 * snippets/sticky-scroll-item.liquid is the first consumer.
 *
 * THE THEME'S SECOND MODAL, AFTER THE CART DRAWER, and deliberately the same
 * shape: page-inert for focus containment, counted scroll lock, `inert` in the
 * markup for the closed state, focus to the panel rather than to a control.
 * Where it differs from assets/cart-drawer.js, the difference is commented.
 *
 * LOAD ORDER: after mobile-menu.js, mega-menu.js, search-overlay.js,
 * cart-drawer.js and filter-drawer.js. Opening this closes all five by calling
 * their `close()` methods directly, so those elements have to be upgraded first.
 *
 * MUTUAL EXCLUSION IS ONE-DIRECTIONAL, and that is a decision rather than an
 * omission. Every other overlay in the theme closes every other overlay both
 * ways. This one does not need the return leg: while it is open, every other
 * child of <body> is inert — including the header — so none of their triggers
 * can be reached by pointer or by keyboard. The five reverse lines would be
 * dead code guarding a state that cannot occur.
 *
 * NO IMAGE IS REQUESTED UNTIL SOMETHING IS OPENED. The <img> is built here from
 * the trigger's data attributes and removed again on close, so a page listing
 * fifty press clippings ships fifty buttons and zero image requests.
 */
if (!customElements.get('image-lightbox')) {
  customElements.define(
    'image-lightbox',
    class ImageLightbox extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('[data-lightbox-panel]');
        this.scrim = this.querySelector('[data-lightbox-scrim]');
        this.figure = this.querySelector('[data-lightbox-figure]');
        this.closeButton = this.querySelector('[data-lightbox-close]');

        if (!this.panel || !this.figure) {
          console.warn('[image-lightbox] markup is incomplete; lightbox disabled');
          return;
        }

        this.isOpen = false;
        this.lastTrigger = null;

        /** Only the elements WE made inert, so closing never clears someone else's. */
        this.inerted = [];

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);

        /**
         * Triggers are found by the id THIS element carries, not by a bare
         * attribute selector. A page can hold more than one sticky-scroll
         * section, each rendering its own lightbox, and a bare
         * `[data-lightbox-trigger]` would make every one of them respond to
         * every button on the page.
         */
        this.triggers = document.querySelectorAll(
          '[data-lightbox-trigger="' + CSS.escape(this.id) + '"]'
        );

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', this.handleTriggerClick);

          /**
           * ARIA IS SET HERE, NOT IN LIQUID. Without this script the button
           * does nothing at all — announcing a dialog that will never open is
           * worse than announcing nothing, and it is worse for exactly the
           * visitors who can least afford it. Same call cart-drawer.js makes.
           */
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.setAttribute('aria-controls', this.id);
        });

        if (this.closeButton) this.closeButton.addEventListener('click', this.handleCloseClick);

        /**
         * CLICK-OUTSIDE IS ONE LISTENER ON THE SCRIM, not a document-level
         * handler doing geometry. The scrim IS everything the visitor can see
         * that is not the picture, so there is nothing to go stale and no
         * "is the click inside the figure" test to get wrong. This works only
         * because the panel above it is pointer-events-none — see the note in
         * snippets/image-lightbox.liquid.
         */
        if (this.scrim) this.scrim.addEventListener('click', this.handleCloseClick);

        document.addEventListener('keydown', this.handleKeydown);
      }

      disconnectedCallback() {
        this.triggers?.forEach((trigger) => {
          trigger.removeEventListener('click', this.handleTriggerClick);
        });
        this.closeButton?.removeEventListener('click', this.handleCloseClick);
        this.scrim?.removeEventListener('click', this.handleCloseClick);
        document.removeEventListener('keydown', this.handleKeydown);

        /**
         * Torn down while open — the theme editor re-rendering this section is
         * the realistic case — would otherwise leave the page scroll-locked and
         * inert, with nothing left alive to undo it.
         */
        if (this.isOpen) {
          this.releaseScroll();
          this.setPageInert(false);
          this.removeAttribute('data-open');
        }
      }

      /**
       * @param {HTMLElement} trigger - The control that was activated; carries the image data.
       */
      open(trigger) {
        if (this.isOpen) return;
        if (!this.render(trigger)) return;

        /**
         * ORDER IS LOAD-BEARING, for the same reason it is in cart-drawer.js:
         * the mobile menu's setPageInert() makes every body child inert except
         * its own and the header's — and this element is its own body child in
         * the flat DOM shape, so it would be inert too. Closing the menu first
         * restores those attributes before we set ours.
         */
        document.querySelector('mobile-menu')?.close?.();
        document.querySelector('mega-menu')?.close?.();
        document.querySelector('search-overlay')?.close?.();
        document.querySelector('cart-drawer')?.close?.();
        document.querySelector('filter-drawer')?.close?.();

        this.setOpen(true);

        // The PANEL, not the Close button — see the note in the Liquid.
        this.panel.focus();
      }

      close() {
        if (!this.isOpen) return;

        this.setOpen(false);

        if (this.lastTrigger && this.lastTrigger.isConnected) this.lastTrigger.focus();
        this.lastTrigger = null;
      }

      /**
       * Build the <img> from the trigger's data attributes.
       *
       * `width` and `height` are set as real attributes so the browser knows the
       * aspect ratio before the bytes arrive — without them the figure is zero
       * high until the image decodes and the scale-in animates from nothing.
       *
       * @returns {boolean} false when the trigger carries no usable source.
       */
      render(trigger) {
        const src = trigger?.dataset.lightboxSrc;
        if (!src) {
          console.warn('[image-lightbox] trigger has no data-lightbox-src');
          return false;
        }

        const image = document.createElement('img');
        image.src = src;
        if (trigger.dataset.lightboxSrcset) image.srcset = trigger.dataset.lightboxSrcset;

        /**
         * `sizes` describes what will actually be PAINTED, not the box it sits
         * in — the rule this theme learned on the mobile menu's backdrop and
         * repeats on every cover image since. Here the picture is capped on both
         * axes, so the painted width is whichever cap binds first: the width cap
         * itself, or the height cap converted through the source's own ratio.
         *
         * `vh`, not `svh`: `sizes` is parsed by the preload scanner before
         * layout and svh support there is not something to rely on. It over-asks
         * slightly on mobile, which is the safe direction. Same call
         * sections/split-hero.liquid makes.
         */
        const ratio = this.aspect(trigger);
        image.sizes =
          '(min-width: 64rem) min(50vw, calc(75vh * ' + ratio + ')), ' +
          'min(90vw, calc(75vh * ' + ratio + '))';

        image.alt = trigger.dataset.lightboxAlt || '';
        if (trigger.dataset.lightboxWidth) image.width = trigger.dataset.lightboxWidth;
        if (trigger.dataset.lightboxHeight) image.height = trigger.dataset.lightboxHeight;
        image.decoding = 'async';

        /**
         * HALF THE SCREEN WIDE ON DESKTOP, THREE QUARTERS OF IT TALL, and
         * `w-auto h-auto object-contain` between them means whichever cap binds
         * first wins and the other axis follows the source's own ratio — so the
         * picture is never cropped and never overflows.
         *
         * THE WIDTH CAP IS 90vw BELOW `lg`, DELIBERATELY, and it is the one place
         * this departs from what was asked for. 50vw of a 390px phone is a 195px
         * press clipping, which is unreadable — the 50% is a desktop composition,
         * not a rule about phones. Flagged in the QA file.
         *
         * These classes are safelisted in assets/css-tailwind.css: this file is
         * not scanned by Tailwind, so nothing here compiles on its own.
         */
        image.className =
          'block h-auto w-auto max-h-[75svh] max-w-[90vw] object-contain lg:max-w-[50vw]';

        /**
         * APPEND THE IMAGE, DO NOT `replaceChildren()` THE FIGURE. The Close
         * button lives inside the figure now — it is positioned off the
         * picture's top-right corner, so it has to share the figure's box —
         * and replacing the figure's children would delete it on first open.
         */
        this.figure.querySelector('img')?.remove();
        this.figure.appendChild(image);
        return true;
      }

      /** Source aspect ratio, defaulting to 1 when the trigger did not say. */
      aspect(trigger) {
        const width = Number(trigger.dataset.lightboxWidth);
        const height = Number(trigger.dataset.lightboxHeight);
        if (!width || !height) return 1;
        return Math.round((width / height) * 1000) / 1000;
      }

      /** The single state funnel. Nothing else touches these attributes. */
      setOpen(next) {
        if (next === this.isOpen) return;
        this.isOpen = next;

        this.toggleAttribute('data-open', next);

        /**
         * The panel is transparent and pointer-events-none when closed, and both
         * of those are still focusable and still read aloud. This is what
         * actually removes it from the tab order and the accessibility tree.
         */
        this.toggleAttribute('inert', !next);

        this.triggers?.forEach((trigger) => {
          trigger.setAttribute('aria-expanded', String(next));
        });

        this.setPageInert(next);

        if (next) {
          this.lockScroll();
          return;
        }

        this.releaseScroll();

        /**
         * Drop the image once the overlay has finished leaving, not on the same
         * tick — emptying the figure immediately would make the picture vanish
         * before the fade-out has run. Under reduced motion the transition is
         * `none`, so `transitionend` never fires and the timeout is the only
         * thing that clears it.
         */
        window.setTimeout(() => {
          if (!this.isOpen) this.figure.querySelector('img')?.remove();
        }, 600);
      }

      /**
       * Make every top-level region of the page inert except this one.
       *
       * A focus trap without a focus trap: no keydown interception, no
       * first/last-focusable bookkeeping that goes stale. The browser simply has
       * nowhere else to send focus.
       *
       * ONE ELEMENT STAYS LIVE, WHERE THE CART KEEPS TWO. assets/cart-drawer.js
       * deliberately leaves <site-header> reachable, because the header is part
       * of the shopping context the drawer sits in. There is nothing in the
       * header a visitor needs while looking at a full-screen picture, and
       * leaving it live would let them open the cart underneath this overlay.
       *
       * Elements already inert are left alone and not recorded, so this composes
       * with another overlay's inerting instead of stealing its state.
       */
      setPageInert(on) {
        if (!on) {
          this.inerted.forEach((element) => element.removeAttribute('inert'));
          this.inerted = [];
          return;
        }

        const keep = this.topLevelAncestor(this);

        Array.prototype.forEach.call(document.body.children, (element) => {
          if (element === keep) return;
          if (element.hasAttribute('inert')) return;

          element.setAttribute('inert', '');
          this.inerted.push(element);
        });
      }

      /** Walk up to whichever child of <body> contains the given node. */
      topLevelAncestor(node) {
        while (node.parentElement && node.parentElement !== document.body) {
          node = node.parentElement;
        }
        return node;
      }

      /**
       * Through the shared controller, per CLAUDE.md — it owns the single Lenis
       * instance and COUNTS locks, so this opening over another overlay cannot
       * unlock the page underneath when it closes.
       *
       * The direct fallback exists because the whole point of the controller is
       * that it might not be there: assets/scroll.js is deferred and can fail to
       * load. Without it the page scrolls behind an open overlay.
       */
      lockScroll() {
        if (window.themeScroll) {
          this.lockedController = true;
          window.themeScroll.lock('image-lightbox');
          return;
        }

        this.lockedController = false;
        document.documentElement.classList.add('overflow-hidden');
      }

      releaseScroll() {
        if (this.lockedController && window.themeScroll) {
          window.themeScroll.unlock('image-lightbox');
        } else {
          document.documentElement.classList.remove('overflow-hidden');
        }
        this.lockedController = false;
      }

      handleTriggerClick(event) {
        /**
         * Modified clicks are let through untouched. On a <button> they do
         * nothing anyway, but the guard costs one line and survives someone
         * later deciding a trigger should be an <a>.
         */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

        event.preventDefault();
        this.lastTrigger = event.currentTarget;
        this.open(event.currentTarget);
      }

      handleCloseClick(event) {
        event.preventDefault();
        this.close();
      }

      handleKeydown(event) {
        if (event.key !== 'Escape' || !this.isOpen) return;
        this.close();
      }
    }
  );
}
